<template>
  <card>
    <card-header :title="$t('profile.password')">
      <p class="card-header-icon" aria-label="more options">
        <mat-icon icon="mdi-pencil" v-if="edit"></mat-icon>
        <a @click="beginEdit()" v-else>{{ $t("editing.edit") }}</a>
      </p>
    </card-header>
    <card-content>
      <Message type="is-danger" ref="message">
        <p v-if="minLifetimeViolationError">
          {{ $t("passwordPolicy.minLifetimeViolationError") }}:
          <strong>{{ passwordUnchangedUntil }}</strong>
        </p>
        <p v-if="incorrectCurrentPassword">
          {{ $t("passwordPolicy.userIncorrectPasswordError") }}
        </p>
        <p v-if="externalProviderPolicyViolation">
          {{ $t("passwordPolicy.externalProviderPolicyViolation") }}
        </p>
        <p v-if="externalProviderError">
          {{ $t("passwordPolicy.externalProviderError") }}
        </p>
        <p v-if="maxAttemptsExceededError">
          {{ $t("passwordPolicy.userLockedUntilError") }}:
          <strong>{{ userLockedUntilDate }}</strong>
        </p>
        <PasswordValidationErrors
            :list="validationErrors"
            v-if="validationErrors"
        ></PasswordValidationErrors>
      </Message>

      <ValidationObserver ref="form">
        <form>
          <fieldset :disabled="!edit">
            <div class="field" v-show="!edit">
              <div class="control">
                <input class="input" type="password" value="**********"/>
              </div>
            </div>
            <template v-if="edit">
              <div class="field">
                <div class="control has-icons-right">
                  <input
                      class="input"
                      :type="showOldPassword ? 'text': 'password'"
                      autocomplete="off"
                      :placeholder="$t('profile.oldPassword')"
                      v-model="form.oldPassword"
                      :class="{ 'is-danger': invalid }"
                      ref="oldPasswordInput"
                  />
                  <span
                      class="icon is-right"
                      @mousedown="down('oldPassword')"
                      @mouseup="up('oldPassword')"
                      style="cursor:pointer;pointer-events:auto;"
                  >
                      <i class="mdi mdi-24px" :class="showOldPassword ? 'mdi-dark mdi-eye-off' :'mdi-eye'"></i>
                  </span>
                  <p class="help is-danger" v-if="invalid">{{ errors[0] }}</p>
                </div>
              </div>
              <div class="field">
                <ValidationProvider
                    :name="$t('profile.password')"
                    :rules="getValidationRules"
                    v-slot="{ errors, invalid }"
                >
                  <div class="control has-icons-right">
                    <input
                        class="input"
                        :type="showPassword ? 'text': 'password'"
                        autocomplete="off"
                        :placeholder="$t('profile.password')"
                        v-model="form.password"
                        :class="{ 'is-danger': invalid || customValidationError }"
                        ref="passwordInput"
                    />
                    <span
                        class="icon is-right"
                        @mousedown="down('password')"
                        @mouseup="up('password')"
                        style="cursor:pointer;pointer-events:auto;"
                    >
                      <i class="mdi mdi-24px" :class="showPassword ? 'mdi-dark mdi-eye-off' :'mdi-eye'"></i>
                    </span>

                    <p class="help is-danger" v-if="invalid || customValidationError">{{ errors[0] || $t(customValidationError)}}</p>
                  </div>
                </ValidationProvider>
              </div>
              <div class="field">
                <ValidationProvider
                    :name="$t('profile.confirm_password')"
                    v-slot="{ errors, invalid }"
                    vid="confirmation"
                >
                  <div class="control has-icons-right">
                    <input
                        class="input"
                        :type="showPasswordConform ? 'text': 'password'"
                        autocomplete="off"
                        :placeholder="$t('profile.confirm_password')"
                        v-model="form.repeatPassword"
                        :class="{ 'is-danger': invalid }"
                        ref="passwordConformInput"
                    />
                    <span
                        class="icon is-right"
                        @mousedown="down('passwordConform')"
                        @mouseup="up('passwordConform')"
                        style="cursor:pointer;pointer-events:auto;"
                    >
                      <i class="mdi mdi-24px" :class="showPasswordConform ? 'mdi-dark mdi-eye-off' :'mdi-eye'"></i>
                    </span>
                    <p class="help is-danger" v-if="invalid">{{ errors[0] }}</p>
                  </div>
                </ValidationProvider>
              </div>

              <div class="field">
                <PasswordPolicyInfo @policyFetched="onPolicyFetched"></PasswordPolicyInfo>
              </div>
            </template>
          </fieldset>
        </form>
      </ValidationObserver>
    </card-content>
    <card-footer v-if="edit">
      <a class="card-footer-item" @click="save()">{{ $t("editing.save") }}</a>
      <a class="card-footer-item" @click="endEdit()">{{
          $t("editing.cancel")
        }}</a>
    </card-footer>
  </card>
</template>

<script>
import profile from "@/services/profile";
import Message from "@/components/Message";
import PasswordValidationErrors from "@/components/PasswordValidationErrors";
import PasswordPolicyInfo from "@/components/PasswordPolicyInfo";
import { extend } from 'vee-validate';
import { oneOf } from 'vee-validate/dist/rules';

extend('onOf', oneOf)

export default {
  components: {
    Message,
    PasswordPolicyInfo,
    PasswordValidationErrors,
  },
  name: "Password",
  data() {
    return {
      form: {
        password: "",
        repeatPassword: "",
        oldPassword: "",
      },
      policy: null,
      edit: false,
      validationErrors: null,
      minLifetimeViolationError: null,
      showPassword: false,
      showPasswordConform: false,
      showOldPassword: false,
      incorrectCurrentPassword: false,
      externalProviderError: false,
      externalProviderPolicyViolation: false,
      maxAttemptsExceededError: null,
      customValidationError: "",
    };
  },
  methods: {
    resetForm() {
      this.form.password = "";
      this.form.repeatPassword = "";
      this.form.oldPassword = "";
      this.$refs.form.reset();
    },
    beginEdit() {
      this.resetForm();
      this.edit = true;
    },
    endEdit() {
      this.edit = false;
      this.$refs["message"].close();
      this.validationErrors = null;
      this.minLifetimeViolationError = null;
      this.maxAttemptsExceededError = null;
    },
    save() {
      this.$refs.form.validate().then((success) => {
        if (!success || this.customValidationError) {
          return;
        }

        profile.setPassword(this.form.password, this.form.oldPassword).then(
            () => {
              this.endEdit();
            },
            (err) => {
              this.validationErrors = err.response.data.validationErrors;
              this.minLifetimeViolationError =
                  err.response.data.minLifetimeViolationError;
              this.incorrectCurrentPassword = err.response.data.incorrectCurrentPassword;
              this.externalProviderError = err.response.data.externalProviderError;
              this.externalProviderPolicyViolation = err.response.data.externalProviderPolicyViolation;
              this.maxAttemptsExceededError = err.response.data.maxAttemptsExceededError;
              if (this.validationErrors ||
                  this.minLifetimeViolationError ||
                  this.incorrectCurrentPassword ||
                  this.externalProviderError ||
                  this.externalProviderPolicyViolation ||
                  this.maxAttemptsExceededError
              ) {
                this.$refs["message"].open();
              }
              console.log(err);
            }
        );
      });
    },
    down(arg) {
      if (arg === "password") {
        this.showPassword = true;
      }
      if (arg === "passwordConform") {
        this.showPasswordConform = true;
      }
      if (arg === "oldPassword") {
        this.showOldPassword = true;
      }
    },
    up(arg) {
      if (arg === "password") {
        this.showPassword = false;
        this.$refs.passwordInput.focus();
      }
      if (arg === "passwordConform") {
        this.showPasswordConform = false;
        this.$refs.passwordConformInput.focus();
      }
      if (arg === "oldPassword") {
        this.showOldPassword = false;
        this.$refs.oldPasswordInput.focus();
      }
    },
    onPolicyFetched(policy) {
      this.policy = policy;
    },
    getPasswordConstraints() {
      if (!this.policy) {
        return null
      }

      const constrNames = [
        "requiredLength",
        "maxLength",
        "requireDigit",
        "requireLowercase",
        "lowercaseAlphabets",
        "requireUppercase",
        "uppercaseAlphabets",
        "requireSpecialCharacters",
        "specialCharacters",
        "allCharactersMustBeUnique",
        "allowSpaces",
        "minPasswordDifference",
      ];
  
      const activeConstr = {};
      constrNames.forEach(constrName => {
        const pwdConstr = this.policy[constrName];
        if (Array.isArray(pwdConstr)) {
          if (pwdConstr.length > 0) {
            activeConstr[constrName] = pwdConstr;
          }
        } else {
          if (pwdConstr) {
            activeConstr[constrName] = pwdConstr;;
          }
        }
      });

      return activeConstr;
    },
    getAlphabetRange(from, to) {
      const startCode = from.charCodeAt(0);
      const endCode = to.charCodeAt(0);
      const codes = [];
      for (let i = startCode; i <= endCode; i++) {
        codes.push(i);
      }
      return String.fromCharCode(...codes);
    },
    parseAlphabets(aplphabets) {
      const letters = new Set();
      aplphabets.forEach(alphabet => {
        const letterGroups = alphabet.letters.split(',');
        letterGroups.forEach(group => {
          group = group.trim()
          group.indexOf('-') != -1 ?
            [...this.getAlphabetRange(group[0], group[2])].forEach(letter => letters.add(letter)):
            letters.add(group);
        });
      });
      return letters;
    },
    runCustomValidation(password) {
      const pwdConstraints = this.getPasswordConstraints();

      // Lowercase require check
      if (pwdConstraints.requireLowercase) {
        const letters = []
        if (pwdConstraints.lowercaseAlphabets) {
          letters.push(...this.parseAlphabets(pwdConstraints.lowercaseAlphabets));
        } else {
          letters.push(...this.getAlphabetRange('a', 'z'));
        }
        const checkRes = [...password].reduce((acc, letter) => {
          if (!acc) {
            acc = letters.includes(letter);
          }
          return acc;
        }, false);
        if (!checkRes) {
          this.customValidationError = 'passwordPolicy.mustContainLowercaseLettersError';
          return;
        }
      }

      // Uppercase require check
      if (pwdConstraints.requireUppercase) {
        const letters = []
        if (pwdConstraints.uppercaseAlphabets) {
          letters.push(...this.parseAlphabets(pwdConstraints.uppercaseAlphabets));
        } else {
          letters.push(...this.getAlphabetRange('A', 'Z'));
        }
        const checkRes = [...password].reduce((acc, letter) => {
          if (!acc) {
            acc = letters.includes(letter);
          }
          return acc;
        }, false);
        if (!checkRes) {
          this.customValidationError = 'passwordPolicy.mustContainUppercaseLettersError';
          return;
        }
      }

      // Special chars require check
      if (pwdConstraints.requireSpecialCharacters) {
        const letters = []
        if (pwdConstraints.specialCharacters) {
          letters.push(...pwdConstraints.specialCharacters.split(',').map(c => c.trim()));
        } else {
          letters.push(...this.getAlphabetRange('!', '/'));
          letters.push(...this.getAlphabetRange(':', '@'));
          letters.push(...this.getAlphabetRange('[', '`'));
          letters.push(...this.getAlphabetRange('{', '~'));
        }
        const checkRes = [...password].reduce((acc, letter) => {
          if (!acc) {
            acc = letters.includes(letter);
          }
          return acc;
        }, false);
        if (!checkRes) {
          this.customValidationError = 'passwordPolicy.mustContainSpecialCharactersError';
          return;
        }
      }

      // Digits require check
      if (pwdConstraints.requireDigit) {
        const letters = []
        letters.push(...this.getAlphabetRange('0', '9'));
        const checkRes = [...password].reduce((acc, letter) => {
          if (!acc) {
            acc = letters.includes(letter);
          }
          return acc;
        }, false);
        if (!checkRes) {
          this.customValidationError = 'passwordPolicy.mustContainDigitsError';
          return;
        }
      }

      // Check all symbols are unique
      if (pwdConstraints.allCharactersMustBeUnique) {
        if (new Set(password).size != password.length) {
          this.customValidationError = 'passwordPolicy.allCharactersMustBeUniqueError';
          return;
        }
      }

      this.customValidationError = '';
    },
  },
  computed: {
    passwordUnchangedUntil() {
      if (
          this.minLifetimeViolationError &&
          this.minLifetimeViolationError.unchangedUntil
      ) {
        const d = new Date(this.minLifetimeViolationError.unchangedUntil);
        const locale = this.$i18n.locale;
        return d.toLocaleDateString(locale) + ":" + d.toLocaleTimeString(locale, {hour: '2-digit', minute: '2-digit'});
      }
      return "";
    },
    userLockedUntilDate() {
      if (
          this.maxAttemptsExceededError &&
          this.maxAttemptsExceededError.UserLockedUntil
      ) {
        const d = new Date(this.maxAttemptsExceededError.UserLockedUntil);
        const locale = this.$i18n.locale;
        return d.toLocaleDateString(locale) + ":" + d.toLocaleTimeString(locale, {hour: '2-digit', minute: '2-digit'});
      }
      return "";
    },
    getValidationRules() {
      const rules = {
        min: 6,
        max: 20,
        confirmed: 'confirmation',
      }

      const pwdConstraints = this.getPasswordConstraints();

      if (pwdConstraints) {
        rules.min = pwdConstraints.requiredLength;
        rules.max = pwdConstraints.maxLength;
      }
      return rules;
    },
  },
  watch: {
    form: {
      handler(val){
        this.runCustomValidation(val.password)
      },
     deep: true
    }
  },
};
</script>
